import { createSlice } from '@reduxjs/toolkit';

// Positions arrive from several places at once: the paged list brings trimmed
// ones, the open status card fetches a full one, and the socket streams both.
// Ids only ever grow for a device, so a newer id always replaces, an older
// one is dropped, and the same id merges attributes so a trimmed copy never
// strips the full one already held.
const mergePosition = (positions, position) => {
  const existing = positions[position.deviceId];
  if (!existing || position.id > existing.id) {
    positions[position.deviceId] = position;
  } else if (position.id === existing.id) {
    positions[position.deviceId] = {
      ...existing,
      ...position,
      attributes: { ...existing.attributes, ...position.attributes },
    };
  }
};

// How many batches a signal log keeps. A reader falls this far behind only
// if the page stops rendering for a long burst.
const signalLogSize = 200;

const appendSignal = (log, ids) => {
  log.seq += 1;
  log.entries.push({ seq: log.seq, ids });
  if (log.entries.length > signalLogSize) {
    log.entries.splice(0, log.entries.length - signalLogSize);
  }
};

// The ids of every entry after `handledSeq`, in order, for a reader of a
// signal log.
export const takeSignals = (log, handledSeq) => log.entries
  .filter((entry) => entry.seq > handledSeq)
  .flatMap((entry) => entry.ids);

const { reducer, actions } = createSlice({
  name: 'session',
  initialState: {
    server: null,
    user: null,
    socket: null,
    positions: {},
    positionsBuffer: [],
    history: {},
    agentLocation: null,
    // What the tiered socket should push: the device ids whose list rows are
    // on screen, the one with an open status card, the user's distance unit
    // for the travel bands, and the filter the marker stream should honour.
    subscription: {
      rows: [], full: [], distanceUnit: null, filter: null,
    },
    // Fleet-wide bucket sizes for the filter menu, fetched once and then
    // pushed over the socket whenever they change.
    counts: null,
    // Per-batch device signals from the stream, each kept as a short log of
    // { seq, ids } entries rather than one slot. Several batches can land
    // before the list gets to read them, and a slot would keep only the
    // last. A reader remembers the last seq it handled and takes every
    // entry after it.
    // Devices that stopped matching the subscribed filter.
    removedDevices: { seq: 0, entries: [] },
    // Devices announced as newly visible (created in or moved into the
    // company while the socket was open) or as newly matching the filter.
    addedDevices: { seq: 0, entries: [] },
    // Devices whose marker came in with a different status from the one the
    // unfiltered marker index held, or that sent their first marker.
    statusChanges: { seq: 0, entries: [] },
  },
  reducers: {
    updateServer(state, action) {
      state.server = action.payload;
    },
    updateUser(state, action) {
      state.user = action.payload;
    },
    updateSocket(state, action) {
      state.socket = action.payload;
    },
    updateAgentLocation(state, action) {
      state.agentLocation = action.payload;
    },
    updatePositions(state, action) {
      action.payload.forEach((position) => {
        state.positionsBuffer.push(position);
      });
    },
    // Positions fetched over REST are wanted on screen now, not after the
    // next buffer flush.
    setPositions(state, action) {
      action.payload.forEach((position) => mergePosition(state.positions, position));
    },
    flushBufferToPositions(state) {
      state.positionsBuffer.forEach((position) => mergePosition(state.positions, position));
      state.positionsBuffer = [];
    },
    updateSubscription(state, action) {
      state.subscription = { ...state.subscription, ...action.payload };
    },
    updateCounts(state, action) {
      state.counts = action.payload;
    },
    updateRemovedDevices(state, action) {
      appendSignal(state.removedDevices, action.payload);
    },
    updateAddedDevices(state, action) {
      appendSignal(state.addedDevices, action.payload);
    },
    updateStatusChanges(state, action) {
      appendSignal(state.statusChanges, action.payload);
    },
  },
});

export { actions as sessionActions };
export { reducer as sessionReducer };
